<template>
  <base-table-wrapper>
    <template #filtering>
      <div>
        <button
          type="button"
          class="btn btn-sm btn-primary fa fa-filter float-right"
          v-b-tooltip.hover :title="$t('buttons.search')"
          v-b-toggle.userdata_search_collapse>
        </button>
      </div>
      <div>
        <b-collapse id="userdata_search_collapse" class="mt-2">
          <UserFiltering @addToSearchParams="setSearchParams($event)"></UserFiltering>
        </b-collapse>
      </div>
    </template>
    <template #body>
        <div v-if="isLoading">
          <Loader size="lg"></Loader>
        </div>
        <div v-else-if="!isLoading && items.length < 1">
          {{ $t('views.user.list.noUser') }}
        </div>
        <div v-else>
            <table class="table table-sm table-striped table-hover">
              <thead>
              <tr>
                <th>{{$t('views.user.list.enabled')}}</th>
                <th>{{$t('views.user.list.username')}}</th>
                <th>{{$t('views.user.list.email')}}</th>
                <th>{{$t('views.user.list.role')}}</th>
                <th colspan="4" />
              </tr>
              </thead>
              <tbody>
              <tr
                v-for="item in items"
                :key="item['@id']">
                <td class="text-left">
                  <i v-if="item.enabled" style="color: #28a745" class="fa fa-check-circle"></i>
                  <i v-else class="fa fa-times-circle" style="color: #dc3545"></i>
                </td>
                <td>
                  <router-link
                    v-if="item"
                    :to="{name: 'UserShow', params: { id: item['@id'] }}">
                  </router-link> {{ item.username }}
                </td>
                <td><a :href="'mailto: ' + item.email">{{ item.email }}</a></td>
                <td class="text-left">
                  {{item.roles[0]}}
                </td>
                <td>
                  <b-dropdown
                    v-if="ableToManageUsers"
                    class="float-right"
                    size="sm" variant="link"
                    right text="Right align"
                    toggle-class="text-decoration-none" no-caret>
                    <template #button-content>
                      <i class="fa fa-bars fa-lg"></i>
                    </template>
                    <b-dropdown-item
                      @click="$router.push({name: 'UserUpdate', params: { id: item['@id'] }})">
                      <i class="fa fa-edit fa-lg"></i>
                      {{ $t('buttons.edit') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      @click="resetPassword(item)">
                      <i class="fa fa-key fa-lg"></i>
                      {{ $t('buttons.resetPassword') }}
                    </b-dropdown-item>
                    <b-dropdown-item v-if="item.enabled"
                      @click="toggleEnabled(item)">
                      <i class="fa fa-key fa-lg"></i>
                      {{ $t('views.user.list.disable') }}
                    </b-dropdown-item>
                    <b-dropdown-item v-else
                      @click="toggleEnabled(item)">
                      <i class="fa fa-key fa-lg"></i>
                      {{ $t('views.user.list.enable') }}
                    </b-dropdown-item>
                  </b-dropdown>
                </td>
              </tr>
              </tbody>
            </table>
            <pagination
              v-if="view"
              :view="view"
              @getPageNumber="getList($event.page, $event.itemsPerPage)"
              :totalItems="totalItems"
            ></pagination>
        </div>
    </template>
  </base-table-wrapper>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Loader from '../../Layout/Loader'
import Pagination from '../../Layout/Pagination'
import ApiService from '@/utils/ApiService'
import { checkPermission } from '@/utils/checkRoles'
import BaseTableWrapper from '@/components/BaseComponents/BaseTableWrapper'
import UserFiltering from '@/components/Dashboard/Views/user/components/UserFiltering/UserFiltering'
import addParamsToQuery from '@/utils/addParamsToQuery'

export default {
  components: {
    UserFiltering,
    Loader,
    Pagination,
    BaseTableWrapper
  },

  data () {
    return {
      searchParams: {
        username: '',
        'userData.name': '',
        'userData.surname': '',
        state: 1
      }
    }
  },
  computed: {
    ableToManageUsers () {
      return checkPermission('appConfiguration', 'AppConfiguration')
    },
    ...mapGetters({
      deletedItem: 'user/del/deleted',
      error: 'user/list/error',
      items: 'user/list/items',
      isLoading: 'user/list/isLoading',
      view: 'user/list/view',
      totalItems: 'user/list/totalItems'
    })
  },

  created () {
    this.getList()
  },
  watch: {
    searchParams: {
      deep: true,
      handler (val) {
        this.getList()
      }
    }
  },
  methods: {
    getList (pageNum = 1, itemsPerPage = undefined) {
      let params = Object.assign({}, this.searchParams)
      params['_page'] = pageNum
      params['itemsPerPage'] = itemsPerPage
      let url = addParamsToQuery('users', params)
      this.getPage(url)
    },
    setSearchParams (params = {}) {
      this.searchParams = Object.assign(this.searchParams, params)
    },
    async resetPassword (user) {
      const decision = await this.$Swal({
        title: this.$t('views.user.list.resetPassword') + user.username,
        text: this.$t('views.user.list.resetPasswordInfo'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('buttons.yes'),
        cancelButtonText: this.$t('buttons.no')
      })
      if (decision) {
        const response = await new ApiService(`users/${user.id}/reset_password`).postSilent({})
        if (response.status === 200) {
          const text = await response.json()
          await this.$Swal({
            type: 'success',
            title: text
          })
        }
      }
    },
    async toggleEnabled (user) {
      let title = ''
      let text = ''
      if (!user.enabled) {
        title = this.$t('views.user.list.enable')
        text = this.$t('views.user.list.enableInfo') + user.username
      } else {
        title = this.$t('views.user.list.disable')
        text = this.$t('views.user.list.disableInfo') + user.username
      }
      const decision = await this.$Swal({
        title: title,
        text: text,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('buttons.yes'),
        cancelButtonText: this.$t('buttons.no')
      })
      if (decision) {
        const response = await new ApiService(`users/${user.id}/toggle_enabled`).postSilent({})
        if (response.status === 200) {
          this.getList()
          const text = await response.json()
          await this.$Swal({
            type: 'success',
            title: text
          })
        }
      }
    },
    ...mapActions({
      getPage: 'user/list/default'
    })
  }
}
</script>
